import { useState } from 'react';
import { NetlifyLogsButton } from '~/components/ui/NetlifyLogsButton';
import { Tooltip } from '~/components/ui/Tooltip';
import { copyToClipboard } from '~/lib/utils/netlify-helpers';
import { InspectDeploymentButton } from './InspectDeploymentButton';
import type { SuccessDisplayProps } from '~/lib/types/netlify';

export const NetlifySuccessDisplay = ({ showRedeployMessage = false }: SuccessDisplayProps) => {
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-white/20 bg-white/5 py-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <div className="i-ph:check-circle-fill w-6 h-6 text-white" />
          <h3 className="text-white text-sm font-semibold">Published Successfully</h3>
        </div>
        <p className="text-[#e7e7e7]/70 text-[11px]">Your site is now live and ready to share</p>
        {showRedeployMessage && (
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="i-ph:circle-notch w-3 h-3 animate-spin text-[#e7e7e7]/70" />
            <p className="text-[#e7e7e7]/70 text-[10px]">Optimizing your application for SEO</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const LinkButtons = ({
  deployedUrl,
  adminUrl,
  deployedId,
  accessToken,
  deployStatus,
  fromRedeploy = false,
}: {
  deployedUrl: string;
  adminUrl: string;
  deployedId: string;
  accessToken: string;
  deployStatus: string;
  fromRedeploy?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(deployedUrl);

    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <div>
      {/* <div className="h-px bg-white/10 mb-4" /> */}
      {fromRedeploy && <div className="text-[#e7e7e7]/70 text-xs mb-2">Below is your published URL</div>}
      <div className="flex gap-2">
        <div
          className="flex-1 flex items-center justify-between gap-2 px-3 py-1 rounded-md 
        border border-white/20 bg-transparent hover:bg-white/5 
        transition-all duration-200"
        >
          <span
            onClick={() => window.open(deployedUrl, '_blank')}
            className="text-xs text-white hover:underline transition-all truncate cursor-pointer font-medium"
          >
            {deployedUrl.length > 30 ? deployedUrl.slice(0, 30) + '...' : deployedUrl}
          </span>
          <Tooltip content={copied ? 'Copied!' : 'Copy'}>
            <button onClick={handleCopy} className="text-[#e7e7e7]/60 hover:text-white p-1 hover:bg-white/10 rounded">
              <div className={copied ? 'i-ph:check w-4 h-4' : 'i-ph:copy w-4 h-4'} />
            </button>
          </Tooltip>
        </div>
        <Tooltip content="Inspect">
          <button
            onClick={() => window.open(adminUrl ?? '', '_blank')}
            className="px-3 py-2 rounded-md border border-white/20 bg-transparent 
          hover:bg-white/5 transition-all duration-200
          text-[#e7e7e7]/70 hover:text-white flex items-center justify-center"
          >
            <div className="i-ph:gear w-4 h-4 flex-shrink-0" />
          </button>
        </Tooltip>
      </div>
      {deployedId && deployStatus === 'ready' && (
        <SuccessLogs
          deployedId={deployedId}
          accessToken={accessToken}
          deployStatus={deployStatus}
          adminUrl={adminUrl}
        />
      )}
    </div>
  );
};

export const SuccessLogs = ({
  deployedId,
  accessToken,
  deployStatus,
  adminUrl,
}: {
  deployedId: string;
  accessToken: string;
  deployStatus: string;
  adminUrl: string;
}) => {
  return (
    <div className="mt-2">
      <NetlifyLogsButton
        deployedId={deployedId}
        accessToken={accessToken}
        deployStatus={deployStatus}
        autoExpand={false}
        adminUrl={adminUrl}
      />
    </div>
  );
};
